/**
 * Element statuses as data: a package is the whole definition of one element status (its stages, what each stage does
 * to the carrier, what a hit of the owner's makes it do), and the engine in `element-status.ts` runs every package the
 * same way.
 *
 * An element status is a neutral parent on the carrier with its effects as bound children of existing kinds, so every
 * reader of slow, root, dot, amp and heal reduction sees an element's effect unchanged. What the parent can do later (a
 * stage's channels, a reaction's bonus damage) is realized once, when it is applied, from the applying cast's frozen
 * facts and stored on the parent; a reaction reads only that.
 */
import { COLD_PACKAGES, LIGHTNING_PACKAGES } from './element-statuses.js';
import type { MechanicalDamageType } from '../types.js';
import { fieldMonsterReferenceMaxHp } from '../reference/field-monster-reference.js';
import { damageComponent, type DamageComponent } from './combat.js';
import { sourceRelativeDamageCore, type SourceSnapshot } from './source-profile.js';

/** PROVISIONAL: potency is the share of a stage's maximum a status buys; 1 buys all of it, so potency is already that fraction. */
export const ELEMENT_POTENCY_REF = 1;

/** The fraction of a stage's maximum an application of `potency` realizes. Continuous, zero at zero, capped at the maximum. */
export function elementPotencyScale(potency: number): number {
  return Math.min(1, potency / ELEMENT_POTENCY_REF);
}

/** A damage magnitude as the engine's other damage leaves state it: written out, or source-relative (`F × H_base + K × H_stat`). */
export type ElementDamageAuthority =
  | { readonly amount: number }
  | { readonly sourceRelative: { readonly f: number; readonly k: number } };

/** What a stage does to its carrier while it lasts. Each channel is a status of its own kind bound to the parent. */
export type ElementChannelSpec =
  | { readonly kind: 'slow'; readonly maxFraction: number }
  | { readonly kind: 'root'; readonly maxDurationS: number }
  | { readonly kind: 'stun'; readonly maxDurationS: number }
  | { readonly kind: 'action_delay'; readonly tickS: number; readonly maxDelayS: number }
  | { readonly kind: 'damage_amp'; readonly maxFraction: number }
  | { readonly kind: 'heal_reduction'; readonly maxFraction: number }
  | { readonly kind: 'dot'; readonly element: MechanicalDamageType; readonly tickS: number; readonly damage: ElementDamageAuthority };

export const ELEMENT_CHANNEL_KINDS = ['slow', 'root', 'stun', 'action_delay', 'damage_amp', 'heal_reduction', 'dot'] as const;

export interface ElementStageSpec {
  readonly id: string;
  /** The parent's lifetime in this stage. A stage with no channels is a lockout. */
  readonly durationS: number;
  readonly channels: readonly ElementChannelSpec[];
  /** A hold's realized duration also bounds its parent and all other channels. */
  readonly lifetimeChannel?: 'root' | 'stun';
}

export type ElementOutcomeSpec =
  | { readonly kind: 'CONSUME' }
  /** Applies `definition` at `stage` to the same carrier for the same owner, from the carrier parent's stored potency and snapshot. */
  | { readonly kind: 'APPLY'; readonly definition: string; readonly stage: string }
  /** One direct damage event on the carrier from the parent's stored snapshot; it is not an attack. `id` is unique in its package. */
  | { readonly kind: 'BONUS_DAMAGE'; readonly id: string; readonly element: MechanicalDamageType; readonly damage: ElementDamageAuthority };

export interface ElementRuleSpec {
  /** Rules are evaluated in id order. */
  readonly id: string;
  /** The stages the rule is live in; absent means every stage. */
  readonly stages?: readonly string[];
  readonly on: {
    /** The owner's hit carries damage of this element. */
    readonly element?: MechanicalDamageType;
    /** The hit's mitigated amount (`ReactionHit.mitigatedAmount`) is at least this fraction of `REACTION_THRESHOLD_HP_BASIS`. */
    readonly minHitFractionOfBasis?: number;
  };
  readonly outcomes: readonly ElementOutcomeSpec[];
  readonly chain?: { readonly jumps: number; readonly rangeM: number };
  readonly outcomesByStage?: Readonly<Record<string, readonly ElementOutcomeSpec[]>>;
}

/**
 * How a further application meets a live parent of the same definition and owner.
 * - `STACK`: one more stack, the lifetime refreshes, the snapshot is the latest application's.
 * - `REFRESH_KEEP`: the lifetime refreshes, the higher stage stays, the snapshot stays.
 * - `REFRESH_REPLACE`: the lifetime refreshes, the snapshot and lease are the latest application's, the schedule stays.
 * - `PER_ENTRY`: every application is a parent of its own.
 */
export type ElementReapply = 'STACK' | 'REFRESH_KEEP' | 'REFRESH_REPLACE' | 'PER_ENTRY';

export interface ElementPackageSpec {
  readonly name: string;
  /** The element of damage a stage must carry to apply this status (the status is what that element's hit leaves behind). */
  readonly element: MechanicalDamageType;
  readonly initialStage: string;
  /** Ordered: a later stage is a higher one. */
  readonly stages: readonly ElementStageSpec[];
  readonly rules: readonly ElementRuleSpec[];
  readonly sameCast: ElementReapply;
  readonly otherCast: ElementReapply;
  readonly maxStacks?: number;
  /** A stack selects the corresponding stage (capped at the last one). */
  readonly stageByStacks?: boolean;
  /** Reactions are enabled once by the stage left at the end of a same-cast episode. */
  readonly reactionsAtEpisodeEnd?: boolean;
  readonly blockedBy?: readonly string[];
  readonly displayName?: string;
  readonly onEnd?: { readonly clear: readonly string[]; readonly apply: string; readonly except: readonly string[] };
}

/** Stage-dependent outcomes have one owner, shared by execution, valuation and burst liability. */
export function elementRuleOutcomes(rule: ElementRuleSpec, stage: string): readonly ElementOutcomeSpec[] {
  return rule.outcomesByStage?.[stage] ?? rule.outcomes;
}

export const elementDefinitionId = (name: string): string => `element:${name}`;

/**
 * PROVISIONAL: the health a hit-strength threshold (a strong enough hit shatters) is a fraction of: the maximum HP of the
 * monster-level model's reference monster at the carrier's level, never the carrier's own, so an elite with twice the HP
 * needs the same absolute hit as a normal monster of its level. The one owner of this basis.
 */
export function REACTION_THRESHOLD_HP_BASIS(carrierLevel: number): number {
  return fieldMonsterReferenceMaxHp(carrierLevel);
}

/** The identity of the cast that made or last stacked a parent: a hit of that cast never triggers the parent. */
export type CastIdentity =
  | { readonly executionId: number }
  | { readonly basicAttack: { readonly playerId: number; readonly attackId: number } };

export function sameCastIdentity(a: CastIdentity, b: CastIdentity): boolean {
  if ('executionId' in a) return 'executionId' in b && a.executionId === b.executionId;
  return 'basicAttack' in b && a.basicAttack.playerId === b.basicAttack.playerId && a.basicAttack.attackId === b.basicAttack.attackId;
}

export type RealizedChannel =
  | { readonly kind: 'slow' | 'damage_amp' | 'heal_reduction'; readonly fraction: number }
  | { readonly kind: 'root'; readonly durationS: number }
  | { readonly kind: 'stun'; readonly durationS: number }
  | { readonly kind: 'action_delay'; readonly tickS: number; readonly maxDelayS: number; readonly scale: number }
  | { readonly kind: 'dot'; readonly tickS: number; readonly amountPerTick: number; readonly component: DamageComponent };

export interface RealizedPackage {
  readonly stages: Readonly<Record<string, readonly RealizedChannel[]>>;
  /** The damage of each bonus-damage outcome, by outcome id. */
  readonly payloads: Readonly<Record<string, DamageComponent>>;
}

/** What one application realized for the package it applies and every package its reactions can apply. Immutable. */
export interface ElementProvenance {
  readonly potency: number;
  readonly packages: Readonly<Record<string, RealizedPackage>>;
}

/** What a parent carries: where it is, whose cast made or last stacked it, and what that cast realized. */
export interface ElementParentState {
  readonly package: string;
  stage: string;
  identity: CastIdentity;
  provenance: ElementProvenance;
}

// ---- registry ---------------------------------------------------------------

/** The shipped element packages; test-only packages are registered by their fixtures. */
const SHIPPED_PACKAGES: readonly ElementPackageSpec[] = [...COLD_PACKAGES, ...LIGHTNING_PACKAGES];

const registry = new Map<string, ElementPackageSpec>(SHIPPED_PACKAGES.map((spec) => [spec.name, spec]));

export function elementPackage(name: string): ElementPackageSpec | undefined {
  return registry.get(name);
}

export function elementPackageNames(): readonly string[] {
  return [...registry.keys()].sort();
}

export function requireElementPackage(name: string): ElementPackageSpec {
  const spec = registry.get(name);
  if (spec === undefined) throw new Error(`ELEMENT_STATUS_UNKNOWN: ${name}`);
  return spec;
}

export function elementStage(spec: ElementPackageSpec, stageId: string): ElementStageSpec {
  const stage = spec.stages.find((candidate) => candidate.id === stageId);
  if (stage === undefined) throw new Error(`ELEMENT_STAGE_UNKNOWN: ${spec.name}/${stageId}`);
  return stage;
}

/** The (package, stage) pairs a stage's reactions can apply. */
export function elementReactionTargets(spec: ElementPackageSpec, stageId: string): readonly { readonly definition: string; readonly stage: string }[] {
  return spec.rules
    .filter((rule) => rule.stages === undefined || rule.stages.includes(stageId))
    .flatMap((rule) => elementRuleOutcomes(rule, stageId).flatMap((outcome) => outcome.kind === 'APPLY' ? [{ definition: outcome.definition, stage: outcome.stage }] : []));
}

function validatePackages(specs: ReadonlyMap<string, ElementPackageSpec>): void {
  for (const spec of specs.values()) {
    if (!spec.stages.some((stage) => stage.id === spec.initialStage)) throw new Error(`ELEMENT_PACKAGE_INVALID: ${spec.name} has no initial stage ${spec.initialStage}`);
    const ruleIds = new Set<string>();
    const payloadIds = new Set<string>();
    for (const rule of spec.rules) {
      if (ruleIds.has(rule.id)) throw new Error(`ELEMENT_PACKAGE_INVALID: ${spec.name} repeats rule ${rule.id}`);
      ruleIds.add(rule.id);
      for (const outcome of [...rule.outcomes, ...Object.values(rule.outcomesByStage ?? {}).flat()]) {
        if (outcome.kind === 'BONUS_DAMAGE') {
          if (payloadIds.has(outcome.id)) throw new Error(`ELEMENT_PACKAGE_INVALID: ${spec.name} repeats outcome ${outcome.id}`);
          payloadIds.add(outcome.id);
        }
        if (outcome.kind === 'APPLY') {
          const target = specs.get(outcome.definition);
          if (target === undefined || !target.stages.some((stage) => stage.id === outcome.stage)) throw new Error(`ELEMENT_PACKAGE_INVALID: ${spec.name}/${rule.id} applies ${outcome.definition}/${outcome.stage}, which does not exist`);
        }
      }
    }
  }
  // What a stage's reactions apply must never lead back to it, or its value and its burst liability would have no end.
  const state = new Map<string, 'OPEN' | 'DONE'>();
  const visit = (name: string, stageId: string, path: readonly string[]): void => {
    const key = `${name}/${stageId}`;
    if (state.get(key) === 'DONE') return;
    if (state.get(key) === 'OPEN') throw new Error(`ELEMENT_PACKAGE_CYCLE: ${[...path, key].join(' -> ')}`);
    state.set(key, 'OPEN');
    for (const target of elementReactionTargets(specs.get(name)!, stageId)) visit(target.definition, target.stage, [...path, key]);
    state.set(key, 'DONE');
  };
  for (const spec of specs.values()) for (const stage of spec.stages) visit(spec.name, stage.id, []);
}

validatePackages(registry);

/** Registers packages for the duration the returned function says; for tests, which keep their own packages out of the shipped list. */
export function registerElementPackages(specs: readonly ElementPackageSpec[]): () => void {
  const next = new Map(registry);
  for (const spec of specs) next.set(spec.name, spec);
  validatePackages(next);
  const added = specs.map((spec) => [spec.name, registry.get(spec.name)] as const);
  for (const spec of specs) registry.set(spec.name, spec);
  return () => { for (const [name, prior] of added) { if (prior === undefined) registry.delete(name); else registry.set(name, prior); } };
}

// ---- realization ------------------------------------------------------------

/** Whether realizing `name` reads the cast's source snapshot: some channel or bonus in it or in what its reactions apply is source-relative. */
export function elementPackageUsesSourceRelative(name: string): boolean {
  const seen = new Set<string>();
  const pending = [name];
  for (let next = pending.pop(); next !== undefined; next = pending.pop()) {
    if (seen.has(next)) continue;
    seen.add(next);
    const spec = requireElementPackage(next);
    for (const stage of spec.stages) for (const channel of stage.channels) if (channel.kind === 'dot' && 'sourceRelative' in channel.damage) return true;
    for (const rule of spec.rules) for (const outcome of [...rule.outcomes, ...Object.values(rule.outcomesByStage ?? {}).flat()]) {
      if (outcome.kind === 'BONUS_DAMAGE' && 'sourceRelative' in outcome.damage) return true;
      if (outcome.kind === 'APPLY') pending.push(outcome.definition);
    }
  }
  return false;
}

/** The cast's frozen facts a damage magnitude is realized against. */
export interface ElementRealizationBasis {
  /** Absent only when every authority a package uses is written out. */
  readonly source?: Pick<SourceSnapshot, 'hBase' | 'hStat'>;
  /** Applied to source-relative damage only, as a damage leaf's is. */
  readonly masteryMultiplier: number;
  readonly stackMultiplier: number;
  /** The cast's outgoing multipliers over one element's amount (a status never crits). */
  readonly outgoing: (amount: number, element: MechanicalDamageType) => DamageComponent;
}

/** The basis of a pricing walk: amounts as the authorities state them, with no cast behind them. */
export const NEUTRAL_REALIZATION_BASIS = (source?: ElementRealizationBasis['source']): ElementRealizationBasis => ({
  ...(source === undefined ? {} : { source }), masteryMultiplier: 1, stackMultiplier: 1, outgoing: (amount, element) => damageComponent(amount, element),
});

function authorityCore(authority: ElementDamageAuthority, basis: ElementRealizationBasis): number {
  if ('amount' in authority) return authority.amount * basis.stackMultiplier;
  if (basis.source === undefined) throw new Error('ELEMENT_STATUS_SOURCE_SNAPSHOT_REQUIRED');
  return sourceRelativeDamageCore(authority.sourceRelative.f, authority.sourceRelative.k, basis.source) * basis.masteryMultiplier * basis.stackMultiplier;
}

function realizeChannel(channel: ElementChannelSpec, scale: number, basis: ElementRealizationBasis): RealizedChannel {
  switch (channel.kind) {
    case 'slow': return { kind: 'slow', fraction: channel.maxFraction * scale };
    case 'damage_amp': return { kind: 'damage_amp', fraction: channel.maxFraction * scale };
    case 'heal_reduction': return { kind: 'heal_reduction', fraction: channel.maxFraction * scale };
    case 'stun':
    case 'root': return { kind: channel.kind, durationS: channel.maxDurationS * scale };
    case 'action_delay': return { kind: 'action_delay', tickS: channel.tickS, maxDelayS: channel.maxDelayS, scale };
    case 'dot': {
      const amountPerTick = authorityCore(channel.damage, basis) * scale;
      return { kind: 'dot', tickS: channel.tickS, amountPerTick, component: basis.outgoing(amountPerTick, channel.element) };
    }
  }
}

/** Realizes `rootName` at `potency` together with every package its reactions can apply. */
export function realizeElementProvenance(rootName: string, potency: number, basis: ElementRealizationBasis): ElementProvenance {
  const scale = elementPotencyScale(potency);
  const packages: Record<string, RealizedPackage> = {};
  const pending = [rootName];
  for (let name = pending.pop(); name !== undefined; name = pending.pop()) {
    if (packages[name] !== undefined) continue;
    const spec = requireElementPackage(name);
    const stages: Record<string, readonly RealizedChannel[]> = {};
    for (const stage of spec.stages) stages[stage.id] = stage.channels.map((channel) => realizeChannel(channel, scale, basis));
    const payloads: Record<string, DamageComponent> = {};
    if (spec.onEnd !== undefined) pending.push(spec.onEnd.apply);
    for (const rule of spec.rules) {
      for (const outcome of [...rule.outcomes, ...Object.values(rule.outcomesByStage ?? {}).flat()]) {
        if (outcome.kind === 'BONUS_DAMAGE') payloads[outcome.id] = basis.outgoing(authorityCore(outcome.damage, basis) * scale, outcome.element);
        if (outcome.kind === 'APPLY') pending.push(outcome.definition);
      }
    }
    packages[name] = { stages, payloads };
  }
  return { potency, packages };
}

/** The lifetime the shared runtime and price read from the same realized channels, before tick quantization. */
export function elementLifetimeS(spec: ElementPackageSpec, stageId: string, provenance: ElementProvenance, susceptibility = 1): number {
  const stage = elementStage(spec, stageId);
  if (stage.lifetimeChannel === undefined) return stage.durationS;
  const hold = provenance.packages[spec.name]!.stages[stageId]!.find((channel) => channel.kind === stage.lifetimeChannel);
  return hold?.kind === 'root' || hold?.kind === 'stun' ? hold.durationS * susceptibility : 0;
}
